import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import crown from "@/assets/crown.png";

const SECTIONS = [
  {
    n: "01",
    title: "The Planner Sponsors the Meal",
    text: "A Planner opens a table — an evening at a popular cafe, dinner at an expensive restaurant, brunch at a premium hotel, afternoon tea at a heritage spot, or a social walk at a cultural landmark. The Planner sponsors the meal and pays the bill physically at the venue.",
  },
  {
    n: "02",
    title: "Deposits Seal the Commitment",
    text: "To publish their commitment, the Planner pays a 40% deposit of the estimated bill securely online via Razorpay — the table goes live instantly. Once the Planner selects a Joinee, the Joinee pays a 25% deposit via UPI, verified manually by our admin. Deposits are promises — proof that both sides will show up.",
  },
  {
    n: "03",
    title: "The House Keeps 5%",
    text: "THE CIRCLE retains a 5% commission on all deposits. It keeps the circle curated, verified and worth belonging to.",
  },
  {
    n: "04",
    title: "Meet, Confirm, Refund",
    text: "After the meetup, both the Planner and the Joinee confirm completion. Deposits are then refunded — 95% of what was paid — manually by our admin via UPI.",
  },
  {
    n: "05",
    title: "The No-Show Penalty",
    text: "If one party does not show up, they forfeit their deposit. The forfeited amount, minus the 5% commission, is transferred to the affected party — handled manually by our admin. Elegance demands accountability.",
  },
];

export default function HowItWorks() {
  const { user, login } = useAuth();

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white" data-testid="how-it-works-page">
      <header className="sticky top-0 z-40 bg-[#0B0B0F]/80 backdrop-blur-xl border-b border-[#D4AF37]/20">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to={user ? "/circle" : "/"}
            data-testid="how-it-works-back-link"
            className="flex items-center gap-2 text-sm text-[#A0A0A0] hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back
          </Link>
          <div className="flex items-center gap-2.5">
            <img src={crown} alt="" className="w-7 h-7" />
            <span className="font-serif font-bold text-base tracking-wide text-[#D4AF37]">THE CIRCLE</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        <img src={crown} alt="" className="w-16 h-16 mb-6 fade-up" />
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-4 fade-up" style={{ animationDelay: "60ms" }}>
          How It Works
        </h1>
        <p className="text-[#A0A0A0] text-base leading-relaxed mb-14 fade-up" style={{ animationDelay: "120ms" }}>
          A ritual of generosity and trust — one sponsored table at a time.
        </p>

        <div className="space-y-12">
          {SECTIONS.map(({ n, title, text }, i) => (
            <section key={n} className="fade-up" style={{ animationDelay: `${180 + i * 80}ms` }} data-testid={`how-section-${n}`}>
              <div className="flex items-baseline gap-4 mb-3">
                <span className="font-serif text-[#D4AF37]/50 text-2xl">{n}</span>
                <h2 className="font-serif text-lg md:text-xl font-semibold text-white">{title}</h2>
              </div>
              <p className="text-[#E0E0E0] text-sm md:text-base leading-relaxed pl-12">{text}</p>
              {i < SECTIONS.length - 1 && <div className="gold-divider mt-12 opacity-40" />}
            </section>
          ))}
        </div>

        <div className="mt-16 text-center">
          {!user && (
            <Button
              onClick={login}
              data-testid="how-it-works-login-cta"
              className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md px-8 h-12 transition-colors"
            >
              Continue with Google
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
